import { useState, useEffect } from "react";
import { userService } from "../../services/userService";
import { searchByFullAddress } from "../../api/valuation";
import { PropertyValuationData } from "../../types/valuation";
import { logger } from "../../utils/logger";
import { useTenant } from "../../contexts/TenantContext";

interface UseContactPropertyFetchProps {
  contactId?: string;
  setPropertyDetails?: (propertyDetails: PropertyValuationData) => void;
}

interface UseContactPropertyFetchResult {
  isLoading: boolean;
  error: string | null;
  propertyFound: boolean;
  propertyDetails: PropertyValuationData | null;
}

/**
 * Hook to fetch property details using contact address information when a contact_id is provided
 * This combines the functionality of useContactAddressAutoFill and valuationService
 */
export const useContactPropertyFetch = ({
  contactId,
  setPropertyDetails,
}: UseContactPropertyFetchProps): UseContactPropertyFetchResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [propertyFound, setPropertyFound] = useState(false);
  const [propertyLoaded, setPropertyLoaded] = useState(false);
  const [propertyDetails, setPropertyDetailsState] =
    useState<PropertyValuationData | null>(null);
  const { config } = useTenant();

  useEffect(() => {
    const fetchContactProperty = async () => {
      // Only fetch once and only if we have a contact ID and config
      if (propertyLoaded || !contactId || !config) return;

      try {
        setIsLoading(true);
        setError(null);

        // Use config.id as the organization ID
        const organizationId = config.id;

        logger.debug("Fetching contact address for property lookup", {
          contactId,
          organizationId,
        });

        // Step 1: Fetch contact information
        const contact = await userService.findContactById(
          contactId,
          organizationId
        );

        if (contact) {
          logger.debug("Contact found, checking for address information", {
            contactId,
          });

          // Step 2: Extract address information
          const address = (contact as any).address || {};

          // Format the address
          const addressParts = [];
          if (address.street) addressParts.push(address.street);
          if (address.city) addressParts.push(address.city);
          if (address.state) addressParts.push(address.state);
          if (address.zip) addressParts.push(address.zip);

          const formattedAddress = addressParts.join(", ");

          if (formattedAddress) {
            logger.debug("Contact address found, fetching property details", {
              contactId,
              address: formattedAddress,
            });

            // Step 3: Fetch property details using the address
            try {
              const propertyData = await searchByFullAddress(formattedAddress);

              logger.debug("Property details found", {
                contactId,
                propertyId: propertyData.id,
              });

              // Set property details
              setPropertyDetailsState(propertyData);
              if (setPropertyDetails) {
                setPropertyDetails(propertyData);
              }

              setPropertyFound(true);
            } catch (propertyError) {
              logger.error("Error fetching property details", {
                contactId,
                address: formattedAddress,
                error:
                  propertyError instanceof Error
                    ? propertyError.message
                    : String(propertyError),
              });

              setError(
                propertyError instanceof Error
                  ? propertyError.message
                  : "Failed to fetch property details"
              );
            }
          } else {
            logger.debug("Contact has no address information", {
              contactId,
            });
            setError("Contact has no address information");
          }
        } else {
          logger.debug("Contact not found", {
            contactId,
          });
          setError("Contact not found");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch contact information";
        logger.error("Error in contact property fetch process", {
          contactId,
          error: errorMessage,
        });
        setError(errorMessage);
      } finally {
        setIsLoading(false);
        setPropertyLoaded(true);
      }
    };

    fetchContactProperty();
  }, [contactId, setPropertyDetails, propertyLoaded, config]);

  return { isLoading, error, propertyFound, propertyDetails };
};

export default useContactPropertyFetch;
